import Modal from 'chameleon/ui-stack/ui/base/modal';
import Button from 'chameleon/ui-stack/ui/base/button';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import AutocompleteSelect from 'chameleon/ui-stack/ui/forms/autocomplete-select';
import Popover from 'chameleon/ui-stack/ui/base/popover';
import dayjs from 'dayjs';
import CheckBox from 'chameleon/ui-stack/ui/base/checkbox';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import './style.scss';
import TagCheckBox from './components/TagCheckBox';
import cn from 'classnames';
import { getTagCategoriesPracticeExam } from '../client';
import QuestionTypesPopover from './components/QuestionTypesPopover';

const thresholdOptions = [
  { label: 'Not Assessed', value: 'NA' },
  { label: 'Below 10%', value: 10 },
  { label: 'Below 20%', value: 20 },
  { label: 'Below 30%', value: 30 },
  { label: 'Below 40%', value: 40 },
  { label: 'Below 50%', value: 50 },
  { label: 'Below 60%', value: 60 },
  { label: 'Below 70%', value: 70 },
  { label: 'Below 80%', value: 80 },
  { label: 'Below 90%', value: 90 },
  { label: 'Below 100%', value: 100 },
];

const GeneratePracticeQuizModal = ({
  setGeneratePracticeQuizModalOpen,
  tags,
  generateQuizParameters,
  setGenerateQuizParameters,
  setIsPracticeQuizSummaryVisible,
  selectedCategory,
  subjectId,
  educationPeriod,
  selectedSectionName,
  selectedStudentName,
}) => {
  const {
    title: titleProp,
    exclusionDays: exclusionDaysProp,
    includeMCQs: includeMCQsProp,
    includeFRQs: includeFRQsProp,
    mcqNum: mcqNumberProp,
    frqNum: frqNumberProp,
    tagsPerformance: selectedTagsProps,
    allowedPracticeExams: selectedPracticeExamTagsProps,
    threshold: thresholdProp,
    wasAllSelected,
    wasAllPracticeExamsSelected,
  } = generateQuizParameters;
  const renderCount = useRef(0);
  const [isFullText, setIsFullText] = useState(true);
  const [title, setTitle] = useState(titleProp || '');
  const [threshold, setThreshold] = useState(
    thresholdProp
      ? thresholdOptions.find(({ value }) => thresholdProp === value)
      : thresholdOptions[4],
  );
  const [isAllSelected, setIsAllSelected] = useState(
    wasAllSelected !== undefined ? wasAllSelected : false,
  );

  const [isAllPracticeExamsSelected, setIsAllPracticeExamsSelected] = useState(
    wasAllPracticeExamsSelected !== undefined
      ? wasAllPracticeExamsSelected
      : false,
  );

  const [exclusionDays, setExclusionDays] = useState(
    exclusionDaysProp > 0 ? exclusionDaysProp : 5,
  );
  const [excludeQuestions, setExcludeQuestions] = useState(
    exclusionDaysProp > 0,
  );
  const [extendedDate, setExtendedDate] = useState(
    dayjs().subtract(exclusionDays, 'day'),
  );
  const [includeMCQs, setIncludeMCQs] = useState(
    includeMCQsProp !== undefined ? includeMCQsProp : true,
  );
  const [includeFRQs, setIncludeFRQs] = useState(
    includeFRQsProp !== undefined ? includeFRQsProp : true,
  );
  const [totalMCQs, setTotalMCQs] = useState(
    mcqNumberProp > 0 ? mcqNumberProp : 0,
  );
  const [totalFRQs, setTotalFRQs] = useState(
    frqNumberProp > 0 ? frqNumberProp : 0,
  );

  const [practiceExamTags, setPracticeExamTags] = useState([]);
  const [selectedPracticeExamTags, setSelectedPracticeExamTags] = useState(
    selectedPracticeExamTagsProps || [],
  );

  const tagsBelowThreshold = React.useMemo(
    () =>
      tags.filter((tag) => {
        return threshold.value !== 'NA'
          ? tag.percentage < threshold.value
          : isNaN(tag.percentage);
      }),
    [tags, threshold],
  );

  const [selectedTags, setSelectedTags] = useState(
    selectedTagsProps?.map((tag) => tag.tagId) || [],
  );

  const onDayChange = useCallback(
    (event) => {
      event.persist();
      setExclusionDays(event.target.value);
      setExtendedDate(dayjs().subtract(exclusionDays, 'day'));
    },
    [exclusionDays],
  );

  const selectAllTags = () => {
    setSelectedTags(
      tags
        .filter((tag) =>
          threshold.value !== 'NA'
            ? tag.percentage < threshold.value
            : isNaN(tag.percentage),
        )
        .map((tag) => tag.id),
    );
  };

  const selectAllPracticeExamTags = () => {
    setSelectedPracticeExamTags(practiceExamTags.map((tag) => tag.id));
  };

  // Get the practice exam tags
  useEffect(async () => {
    if (subjectId) {
      const tagCategoriesPracticeExam = await getTagCategoriesPracticeExam({
        subjectId,
        educationPeriod,
      });
      setPracticeExamTags(tagCategoriesPracticeExam);
    }
  }, [subjectId]);

  useEffect(() => {
    if (renderCount.current > 1) {
      if (isAllSelected) {
        selectAllTags();
      } else {
        setSelectedTags([]);
      }
    }
  }, [isAllSelected]);

  useEffect(() => {
    if (renderCount.current > 1) {
      if (isAllPracticeExamsSelected) {
        selectAllPracticeExamTags();
      } else {
        setSelectedPracticeExamTags([]);
      }
    }
  }, [isAllPracticeExamsSelected]);

  useEffect(() => {
    if (renderCount.current > 1 || !selectedTagsProps) {
      selectAllTags(); // When threshold is changed, select all tags below threshold
      setIsAllSelected(true);
    }
  }, [threshold]);

  useEffect(() => {
    /* This hooks sets the properties if previous state needs to be persisted
    (In case of re-opening the modal)
     */
    setSelectedTags(selectedTagsProps?.map((tag) => tag.tagId) || []);
  }, []);

  const toggleSelectAll = () => {
    setIsAllSelected(!isAllSelected);
  };

  const toggleSelectAllPracticeExams = () => {
    setIsAllPracticeExamsSelected(!isAllPracticeExamsSelected);
  };

  const selectTag = (tagId) => {
    setSelectedTags([...selectedTags, tagId]);
  };

  const selectPracticeExamTag = (tagId) => {
    setSelectedPracticeExamTags([...selectedPracticeExamTags, tagId]);
  };

  const unselectTag = (tagId) => {
    setSelectedTags(selectedTags.filter((id) => id !== tagId));
  };

  const unselectPracticeExamTag = (tagId) => {
    setSelectedPracticeExamTags(
      selectedPracticeExamTags.filter((id) => id !== tagId),
    );
  };

  const gatherQuizParams = () => ({
    title,
    threshold: threshold.value,
    exclusionDays: excludeQuestions ? exclusionDays : 0,
    wasAllSelected: isAllSelected,
    wasAllPracticeExamsSelected: isAllPracticeExamsSelected,
    includeMCQs,
    includeFRQs,
    mcqNum: includeMCQs ? totalMCQs : 0,
    frqNum: includeFRQs ? totalFRQs : 0,
    tagsPerformance: tags
      .filter((tag) => selectedTags.includes(tag.id))
      .filter((tag) =>
        threshold.value !== 'NA'
          ? tag.percentage < threshold.value
          : isNaN(tag.percentage),
      )
      .map(({ id, percentage }) => ({
        tagId: id,
        performance: isNaN(percentage) ? -1 : percentage,
      })),
    allowedPracticeExams: selectedPracticeExamTags,
  });

  renderCount.current++; // Incrementing on each render

  const handleViewQuizSummary = () => {
    setGenerateQuizParameters(gatherQuizParams());
    setGeneratePracticeQuizModalOpen(false);
    setIsPracticeQuizSummaryVisible(true);
  };

  return (
    <Modal
      size="medium"
      title={`Generate Practice Quiz based on ${selectedSectionName} / ${selectedStudentName}`}
      onClose={() => setGeneratePracticeQuizModalOpen(false)}
      dialogClassName="generate-practice-quiz-modal"
      bottomContent={
        <div className="flex justify-end p-5 border-top">
          <div>
            <Button
              isNaked
              accessibility={{
                'data-test-id': 'button-secondary',
              }}
              onClick={() => setGeneratePracticeQuizModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
          <div>
            <Button
              isPrimary
              disabled={
                !title ||
                !selectedTags.length ||
                (!includeMCQs && !includeFRQs) ||
                (includeMCQs && totalMCQs <= 0) ||
                (includeFRQs && totalFRQs <= 0)
              }
              accessibility={{
                'data-test-id': 'button-secondary',
              }}
              onClick={() => handleViewQuizSummary()}
            >
              View Summary
            </Button>
          </div>
        </div>
      }
    >
      <div className="pb-4">
        <label className="text-gray-900 font-bold font-medium">Quiz Name</label>
      </div>
      <input
        type="text"
        placeholder="Enter quiz name"
        className="border border-solid border-gray-900 rounded-full w-full h-12.2 text-gray-900 pl-4 mb-8"
        onChange={(e) => setTitle(e.target.value)}
        value={title}
      />
      <div className="pb-4">
        <label className="text-gray-900 font-bold font-medium">
          Based on {selectedCategory} Performance
        </label>
      </div>
      <AutocompleteSelect
        inputClassName="border-gray-900"
        options={thresholdOptions}
        onChange={({ value }) => {
          setThreshold(
            thresholdOptions.find((option) => option.value === value),
          );
        }}
        value={threshold}
        trackingId="threshold-selector"
      />
      {/* Units Selector */}
      <div className="pt-8 pb-4">
        <label className="text-gray-900 font-bold font-medium">
          {selectedCategory} values to Include
        </label>
      </div>
      <Popover
        hideArrow
        position="bottom"
        size="medium"
        withPortal
        contentClassName="tag-selector-popover"
        trigger={
          <ButtonWithIcon
            isOutline
            background="white"
            className="w-full justify-between"
            containerClassName="w-full items-center"
            icon="arrow_drop_down"
            iconOrientation="right"
          >
            {selectedTags.length > 0
              ? `${selectedTags.length} ${selectedCategory} values selected`
              : 'Select'}
          </ButtonWithIcon>
        }
      >
        <div className="py-4">
          <Button
            isSecondary
            className="mr-5"
            onClick={toggleSelectAll}
            accessibility={{
              'aria-label': `Unselect All`,
            }}
          >
            {isAllSelected ? 'Unselect All' : 'Select All'}
          </Button>
          <Button
            isSecondary
            onClick={() => setIsFullText(!isFullText)}
            accessibility={{
              'aria-label': `Show ${isFullText ? 'labels only' : 'full text'}`,
            }}
          >
            {isFullText ? 'Show labels only' : 'Show Full Text'}
          </Button>
        </div>
        {tagsBelowThreshold.map((tag) => (
          <TagCheckBox
            key={tag.id}
            isFullText={isFullText}
            tag={tag}
            isCheckedProp={selectedTags.includes(tag.id)}
            selectTag={selectTag}
            unselectTag={unselectTag}
          />
        ))}
      </Popover>
      {/* end units selector */}
      {/* Units Selector */}
      <div className="pt-8 pb-4">
        <label className="text-gray-900 font-bold font-medium">
          Allow Secure Practice Exam Questions from
        </label>
      </div>
      <Popover
        hideArrow
        position="bottom"
        size="medium"
        withPortal
        contentClassName="tag-selector-popover"
        trigger={
          <ButtonWithIcon
            isOutline
            background="white"
            className="w-full justify-between"
            containerClassName="w-full items-center"
            icon="arrow_drop_down"
            iconOrientation="right"
          >
            {practiceExamTags.length > 0
              ? `${selectedPracticeExamTags.length} practice exams selected`
              : 'Select'}
          </ButtonWithIcon>
        }
      >
        <div className="py-4">
          <Button
            isSecondary
            className="mr-5"
            onClick={toggleSelectAllPracticeExams}
            accessibility={{
              'aria-label': `Unselect All`,
            }}
          >
            {isAllPracticeExamsSelected ? 'Unselect All' : 'Select All'}
          </Button>
        </div>
        {practiceExamTags.map((tag) => (
          <TagCheckBox
            key={tag.id}
            isFullText={isFullText}
            tag={tag}
            isCheckedProp={selectedPracticeExamTags.includes(tag.id)}
            selectTag={selectPracticeExamTag}
            unselectTag={unselectPracticeExamTag}
            isPracticeExamTag
          />
        ))}
      </Popover>
      {/* end practice exams selector */}
      <div className="flex gap-0.7 pt-8 items-center mb-6">
        <CheckBox
          checked={excludeQuestions}
          accessibility={{
            ariaLabelledBy: 'exclude-questions',
          }}
          className={cn('shrink-0')}
          onClick={() => setExcludeQuestions(!excludeQuestions)}
        />
        <label id="exclude-questions" className={cn('text-14')}>
          Exclude questions assigned to classes in the past
        </label>
        <div className="relative ml-auto pb-4">
          <div className="flex text-16 text-gray-900">
            <input
              aria-label="Number of days to be excluded"
              min="1"
              max="365"
              type="number"
              className={cn(
                'w-20.2 text-16 text-center border border-solid border-gray-400 rounded-l-full h-12.2 pl-4',
                {
                  'text-gray-400': !excludeQuestions,
                },
              )}
              onChange={onDayChange}
              disabled={!excludeQuestions}
              value={exclusionDays}
            />
            <div
              className={cn(
                'flex items-center h-12.2 text-16 border border-l-none border-solid border-gray-400 rounded-r-full px-4',
                {
                  'text-gray-400': !excludeQuestions,
                },
              )}
            >
              Day(s)
            </div>
          </div>
          <div
            className={cn(
              'absolute -bottom-8 right-4 text-gray-600 text-12 mt-0.8 text-right',
              {
                'text-gray-400': !excludeQuestions,
              },
            )}
          >
            after {extendedDate.format('MM/DD/YY')}
          </div>
        </div>
      </div>
      <div class="flex items-center">
        <label className="inline flex text-gray-900 font-bold font-medium">
          Question Types
        </label>
        <QuestionTypesPopover />
      </div>
      <div className="flex gap-0.7 pt-8 items-center">
        <CheckBox
          checked={includeMCQs}
          accessibility={{
            ariaLabelledBy: 'exclude-questions',
          }}
          className={cn('shrink-0')}
          onClick={() => setIncludeMCQs(!includeMCQs)}
        />
        <label id="exclude-questions" className={cn('text-14')}>
          MCQs
        </label>
        <div className="relative ml-auto">
          <div className="flex text-16 text-gray-900">
            <input
              aria-label="Number of MCQs to be included"
              min="1"
              max="100"
              type="number"
              className={cn(
                'w-20.2 text-16 text-center border border-solid border-gray-400 rounded-l-full h-12.2 pl-4',
                {
                  'text-gray-400': !includeMCQs,
                },
              )}
              onChange={(e) => setTotalMCQs(e.target.value)}
              disabled={!includeMCQs}
              value={totalMCQs}
            />
            <div
              className={cn(
                'flex items-center h-12.2 text-16 border border-l-none border-solid border-gray-400 rounded-r-full px-4',
                {
                  'text-gray-400': !includeMCQs,
                },
              )}
            >
              questions
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-0.7 pt-8 items-center">
        <CheckBox
          checked={includeFRQs}
          accessibility={{
            ariaLabelledBy: 'exclude-questions',
          }}
          className={cn('shrink-0')}
          onClick={() => setIncludeFRQs(!includeFRQs)}
        />
        <label id="exclude-questions" className={cn('text-14')}>
          FRQs
        </label>
        <div className="relative ml-auto">
          <div className="flex text-16 text-gray-900">
            <input
              aria-label="Number of FRQs to be included"
              min="1"
              max="100"
              type="number"
              className={cn(
                'w-20.2 text-16 text-center border border-solid border-gray-400 rounded-l-full h-12.2 pl-4',
                {
                  'text-gray-400': !includeFRQs,
                },
              )}
              onChange={(e) => setTotalFRQs(e.target.value)}
              disabled={!includeFRQs}
              value={totalFRQs}
            />
            <div
              className={cn(
                'flex items-center h-12.2 text-16 border border-l-none border-solid border-gray-400 rounded-r-full px-4',
                {
                  'text-gray-400': !includeFRQs,
                },
              )}
            >
              questions
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GeneratePracticeQuizModal;
